import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Save, Edit3, ExternalLink, X } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGIN_LOGO = "https://customer-assets.emergentagent.com/job_wix-site-builder-2/artifacts/ax61agh9_ChatGPT%20Image%20Jul%2023%2C%202025%20at%2008_02_13%20PM.png";

/**
 * MS-DOS-style login screen for the LOG-IN tab. Hard-coded credentials in backend:
 *   user: "Me Only"  /  pass: "123456789"
 * On success, renders the BlockchainDashboard with 9 editable app link tiles.
 */
export default function LoginScreen({ section, onUpdated, onBackToHub }) {
    const [authed, setAuthed] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const userRef = useRef(null);

    useEffect(() => {
        // Restore session from localStorage
        try {
            if (localStorage.getItem("caprio_login_token")) setAuthed(true);
        } catch (_) {}
    }, []);

    useEffect(() => {
        if (!authed) setTimeout(() => userRef.current?.focus(), 200);
    }, [authed]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const res = await axios.post(`${API}/auth/login`, { username, password });
            if (res.data?.ok) {
                try { localStorage.setItem("caprio_login_token", res.data.token); } catch (_) {}
                setAuthed(true);
            } else {
                setError("> ACCESS DENIED. PLEASE TRY AGAIN.");
                setPassword("");
            }
        } catch (err) {
            setError("> CONNECTION ERROR. RETRY.");
        } finally {
            setBusy(false);
        }
    };

    const handleLogout = () => {
        try { localStorage.removeItem("caprio_login_token"); } catch (_) {}
        setAuthed(false);
        setUsername(""); setPassword("");
    };

    if (authed) {
        return <BlockchainDashboard section={section} onUpdated={onUpdated} onLogout={handleLogout} onBackToHub={onBackToHub} />;
    }

    return (
        <div
            data-testid="login-screen"
            className="absolute inset-0 z-30 flex items-center justify-center px-4"
            style={{
                background: "#000",
                // CRT scanline texture
                backgroundImage:
                    "repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.45) 3px), radial-gradient(ellipse at center, #0c0805 0%, #000 80%)",
                fontFamily: "'Courier New', ui-monospace, monospace",
            }}
        >
            {/* Scanline + flicker overlay */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(255,170,40,0.05) 0%, rgba(255,170,40,0) 8%, rgba(255,170,40,0) 92%, rgba(255,170,40,0.05) 100%)",
                    mixBlendMode: "screen",
                }}
            />

            <div className="relative w-full max-w-md fade-in">
                {/* Back to Center Hub */}
                {onBackToHub && (
                    <button
                        type="button"
                        onClick={onBackToHub}
                        data-testid="login-back-to-hub"
                        className="absolute -top-12 left-0 flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase px-3 py-2 rounded-full"
                        style={{
                            background: "rgba(20,12,4,0.65)",
                            color: "#FFA828",
                            border: "1px solid rgba(255,170,40,0.55)",
                            fontFamily: "'Courier New', monospace",
                        }}
                    >
                        ← Center Hub
                    </button>
                )}

                {/* Logo card mirroring user's reference */}
                <div className="text-center mb-6">
                    <img
                        src={LOGIN_LOGO}
                        alt="Log-In"
                        data-testid="login-logo"
                        className="mx-auto w-[260px] md:w-[320px] rounded-xl"
                        style={{
                            border: "1px solid rgba(212,175,55,0.35)",
                            boxShadow: "0 0 60px rgba(212,175,55,0.18)",
                        }}
                    />
                </div>

                <form
                    onSubmit={handleSubmit}
                    data-testid="login-form"
                    className="rounded-md p-5"
                    style={{
                        background: "rgba(20,12,4,0.6)",
                        border: "1px solid rgba(255,170,40,0.45)",
                        boxShadow: "inset 0 0 28px rgba(255,170,40,0.08)",
                    }}
                >
                    <div className="text-[11px] tracking-widest mb-3" style={{ color: "#FFA828" }}>
                        C:\CAPRIO&gt; LOGIN.EXE
                    </div>
                    <div className="text-[11px] tracking-wider mb-1" style={{ color: "#FFA828" }}>
                        ENTER USERNAME:
                    </div>
                    <input
                        ref={userRef}
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        data-testid="login-username"
                        autoComplete="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        className="w-full bg-transparent outline-none border-b py-2 mb-4 caret-amber-400"
                        style={{
                            color: "#FFC85C",
                            borderColor: "rgba(255,170,40,0.5)",
                            fontFamily: "'Courier New', monospace",
                            fontSize: 16,
                            letterSpacing: 1.2,
                        }}
                    />
                    <div className="text-[11px] tracking-wider mb-1" style={{ color: "#FFA828" }}>
                        ENTER PASSWORD:
                    </div>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="login-password"
                        className="w-full bg-transparent outline-none border-b py-2 mb-5 caret-amber-400"
                        style={{
                            color: "#FFC85C",
                            borderColor: "rgba(255,170,40,0.5)",
                            fontFamily: "'Courier New', monospace",
                            fontSize: 16,
                            letterSpacing: 4,
                        }}
                    />
                    {error && (
                        <div
                            data-testid="login-error"
                            className="mb-3 text-xs"
                            style={{ color: "#FF6A55" }}
                        >
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="login-submit"
                        className="w-full py-3 rounded-full uppercase tracking-[0.3em] text-[11px] disabled:opacity-50"
                        style={{
                            background: "linear-gradient(180deg, #F0A52C, #B4720F)",
                            color: "#fff8e7",
                            boxShadow: "0 0 18px rgba(255,170,40,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                            fontFamily: "'Courier New', monospace",
                            border: "1px solid rgba(255,170,40,0.85)",
                        }}
                    >
                        {busy ? "Authenticating…" : "Log in"}
                    </button>

                    <div className="mt-5 text-center text-[10px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,170,40,0.7)" }}>
                        League of Geeks · Influential Network
                    </div>
                </form>
            </div>
        </div>
    );
}

/**
 * Blockchain-style dashboard — 9 hex-shaped link tiles arranged in a chain layout.
 * Each tile is editable inline (title + URL).
 */
function BlockchainDashboard({ section, onUpdated, onLogout, onBackToHub }) {
    const accent = "#D4AF37";
    const { isAdmin } = useAdmin();
    const links = section?.links?.length
        ? section.links
        : Array.from({ length: 9 }).map((_, i) => ({ title: `App ${i + 1}`, url: "" }));

    const [editing, setEditing] = useState(null); // index | null
    const [draftTitle, setDraftTitle] = useState("");
    const [draftUrl, setDraftUrl] = useState("");
    const [saving, setSaving] = useState(false);

    const openEdit = (i) => {
        setEditing(i);
        setDraftTitle(links[i]?.title || "");
        setDraftUrl(links[i]?.url || "");
    };

    const saveEdit = async () => {
        if (editing === null) return;
        const next = [...links];
        next[editing] = { title: draftTitle.trim() || `App ${editing + 1}`, url: draftUrl.trim() };
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, { links: next });
            onUpdated && onUpdated(res.data);
            setEditing(null);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    return (
        <div
            data-testid="blockchain-dashboard"
            className="absolute inset-0 z-30 overflow-y-auto px-4 pt-32 pb-16"
            style={{
                background: "radial-gradient(ellipse at top, #0c1322 0%, #02040a 70%)",
            }}
        >
            <div className="absolute top-24 right-6 z-40 flex items-center gap-2">
                {onBackToHub && (
                    <button
                        type="button"
                        onClick={onBackToHub}
                        data-testid="blockchain-back-to-hub"
                        className="px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                        style={{
                            background: accent,
                            color: "#0a0a0a",
                            boxShadow: `0 8px 22px -10px ${accent}`,
                        }}
                    >
                        ← Center Hub
                    </button>
                )}
                <button
                    type="button"
                    onClick={onLogout}
                    data-testid="blockchain-logout"
                    className="px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}55`,
                        color: accent,
                    }}
                >
                    Log Out
                </button>
            </div>

            <div className="max-w-6xl mx-auto fade-in">
                <div className="text-center mb-10">
                    <div className="text-[10px] tracking-[0.5em] uppercase mb-3" style={{ color: accent }}>
                        Caprio Jackson Experience
                    </div>
                    <h1
                        className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none"
                        style={{ color: "#fff" }}
                    >
                        The Chain
                    </h1>
                    <p className="mt-4 text-sm font-light max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Nine private app links — each a node in the chain. Click the pencil to edit a node's title and destination.
                    </p>
                </div>

                {/* Hex grid 3x3 with connecting lines */}
                <div className="relative grid grid-cols-3 gap-6 md:gap-10 max-w-3xl mx-auto" data-testid="blockchain-grid">
                    {/* Connector lines (decorative) */}
                    <svg
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        style={{ zIndex: 0, opacity: 0.3 }}
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                    >
                        <line x1="16" y1="16" x2="50" y2="16" stroke={accent} strokeWidth="0.3" />
                        <line x1="50" y1="16" x2="84" y2="16" stroke={accent} strokeWidth="0.3" />
                        <line x1="16" y1="50" x2="50" y2="50" stroke={accent} strokeWidth="0.3" />
                        <line x1="50" y1="50" x2="84" y2="50" stroke={accent} strokeWidth="0.3" />
                        <line x1="16" y1="84" x2="50" y2="84" stroke={accent} strokeWidth="0.3" />
                        <line x1="50" y1="84" x2="84" y2="84" stroke={accent} strokeWidth="0.3" />
                        <line x1="16" y1="16" x2="16" y2="84" stroke={accent} strokeWidth="0.3" />
                        <line x1="50" y1="16" x2="50" y2="84" stroke={accent} strokeWidth="0.3" />
                        <line x1="84" y1="16" x2="84" y2="84" stroke={accent} strokeWidth="0.3" />
                    </svg>

                    {Array.from({ length: 9 }).map((_, i) => {
                        const link = links[i] || { title: `App ${i + 1}`, url: "" };
                        const hasUrl = !!link.url;
                        return (
                            <div
                                key={i}
                                data-testid={`chain-node-${i}`}
                                className="relative rounded-xl p-4 group transition-all hover:scale-[1.02]"
                                style={{
                                    background: "rgba(10,14,28,0.7)",
                                    border: `1px solid ${accent}44`,
                                    boxShadow: hasUrl ? `0 0 30px ${accent}22` : "none",
                                    minHeight: 130,
                                }}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <span
                                        className="text-[10px] tracking-[0.25em] uppercase"
                                        style={{ color: accent }}
                                    >
                                        Node {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {isAdmin && (
                                        <button
                                            type="button"
                                            onClick={() => openEdit(i)}
                                            data-testid={`chain-edit-${i}`}
                                            className="opacity-50 hover:opacity-100"
                                            style={{ color: accent }}
                                        >
                                            <Edit3 size={12} />
                                        </button>
                                    )}
                                </div>
                                <div
                                    className="font-serif-display text-xl uppercase tracking-wide leading-tight mb-3"
                                    style={{ color: "#fff" }}
                                    data-testid={`chain-title-${i}`}
                                >
                                    {link.title || `App ${i + 1}`}
                                </div>
                                {hasUrl ? (
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`chain-link-${i}`}
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase"
                                        style={{ color: accent }}
                                    >
                                        Open <ExternalLink size={10} />
                                    </a>
                                ) : (
                                    <span
                                        className="text-[10px] tracking-[0.25em] uppercase"
                                        style={{ color: "rgba(255,255,255,0.35)" }}
                                    >
                                        — empty —
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Edit modal */}
            {editing !== null && (
                <div
                    data-testid="chain-edit-modal"
                    className="fixed inset-0 z-50 flex items-center justify-center px-4"
                    style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
                >
                    <div
                        className="glass rounded-2xl p-6 w-full max-w-md"
                        style={{ border: `1px solid ${accent}55` }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                                Edit Node {editing + 1}
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                data-testid="chain-edit-close"
                                style={{ color: "rgba(255,255,255,0.7)" }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>Title</div>
                        <input
                            type="text"
                            value={draftTitle}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            data-testid="chain-edit-title"
                            className="w-full mb-4 rounded-xl px-4 py-2 outline-none text-sm"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mb-1" style={{ color: "#fff" }}>URL</div>
                        <input
                            type="url"
                            value={draftUrl}
                            onChange={(e) => setDraftUrl(e.target.value)}
                            placeholder="https://"
                            data-testid="chain-edit-url"
                            className="w-full mb-5 rounded-xl px-4 py-2 outline-none text-sm"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        <button
                            type="button"
                            onClick={saveEdit}
                            disabled={saving}
                            data-testid="chain-edit-save"
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase disabled:opacity-50"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <Save size={14} /> {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
